// `/respond/{co}/{token}` (M8-ASSESS): the respondent's questionnaire, reached by the private link
// a manager copied. No session is needed (members and guests alike): the token is the key.
// One column, one step at a time over the view's `steps` — role and clarity, tasks, each task's
// hours / energy / enjoyment / skill / area (and why, on the full X-Ray), the week's capacity,
// the team (managers), what slips — with the consent line (`visibility`) up top. Edits autosave
// (respond.save, a second after the last change, and on every step change); Submit sends the named
// tasks, and a 422 lands on the first step with an error, its messages inline. A submitted
// questionnaire shows the thank-you; an expired link (410) and an unknown one (404) say so.
import { ApiError } from "@companyos/sdk";
import {
  BrandMark,
  Button,
  EmptyState,
  Input,
  Label,
  Notice,
  Progress,
  Spinner,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CircleCheckIcon,
  LinkIcon,
  PlusIcon,
  ShieldCheckIcon,
  Trash2Icon,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import {
  type Answers,
  blankTask,
  editTask,
  namedTasks,
  type QuestionnaireView,
  STEP_LABEL,
  type Step,
  stepOfError,
  stepReady,
} from "./assess-model";
import { assessKeys, respondQuery } from "./assess-queries";

type FieldError = { path: string; message: string };

function Frame({ children, company }: { children: ReactNode; company?: string }) {
  return (
    <main
      id="main"
      className="min-h-svh bg-background px-4 py-8 text-foreground max-md:py-5"
      data-slot="respond-page"
    >
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5">
        <div className="flex items-center gap-2.5" data-slot="wordmark">
          <BrandMark name={company ?? "CompanyOS"} size={32} />
          <span className="truncate font-heading text-base font-strong">
            {company ?? "CompanyOS"}
          </span>
        </div>
        {children}
      </div>
    </main>
  );
}

export function RespondPage({ api, co, token }: { api: Api; co: string; token: string }) {
  const view = useQuery(respondQuery(api, co, token));
  if (view.isPending)
    return (
      <Frame>
        <div
          className="flex justify-center py-16"
          role="status"
          aria-label="Loading the questionnaire"
        >
          <Spinner />
        </div>
      </Frame>
    );
  if (view.isError) {
    const status = view.error instanceof ApiError ? view.error.status : 0;
    return (
      <Frame>
        <EmptyState
          variant="card"
          icon={LinkIcon}
          title={
            status === 410
              ? "This link has expired"
              : status === 404
                ? "This link doesn't work"
                : "The questionnaire could not be loaded"
          }
          description={
            status === 410
              ? "Ask the person who sent it for a fresh link; your saved answers are kept."
              : status === 404
                ? "It may have been replaced by a newer link, or the round has closed. Ask the person who sent it."
                : "Check your connection and reload the page."
          }
        />
      </Frame>
    );
  }
  if (view.data.state === "completed")
    return (
      <Frame company={view.data.company_name}>
        <Done view={view.data} />
      </Frame>
    );
  return (
    <Frame company={view.data.company_name}>
      <Questionnaire api={api} co={co} token={token} view={view.data} />
    </Frame>
  );
}

function Done({ view }: { view: QuestionnaireView }) {
  return (
    <EmptyState
      variant="card"
      icon={CircleCheckIcon}
      title="Thank you — your answers are in"
      description={`${view.round_name}${view.submitted_at ? ` · sent ${formatDateTime(view.submitted_at)}` : ""}. ${view.visibility}`}
    />
  );
}

function Questionnaire({
  api,
  co,
  token,
  view,
}: {
  api: Api;
  co: string;
  token: string;
  view: QuestionnaireView;
}) {
  const qc = useQueryClient();
  const steps = view.steps;
  const full = view.questionnaire.key === "full";
  const [answers, setAnswers] = useState<Answers>(() =>
    view.answers.tasks.length ? view.answers : { ...view.answers, tasks: [blankTask()] },
  );
  const [at, setAt] = useState(0);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [dirty, setDirty] = useState(false);
  const top = useRef<HTMLDivElement>(null);
  const step = steps[at] as Step;
  const last = at === steps.length - 1;

  const save = useMutation({
    mutationFn: (a: Answers) =>
      api.client.respond.save({ params: { co, token }, body: { answers: a } }),
    onSuccess: () => setDirty(false),
  });
  const submit = useMutation({
    mutationFn: (a: Answers) =>
      api.client.respond.submit({ params: { co, token }, body: { answers: a } }),
    onSuccess: (next) => qc.setQueryData(assessKeys.respond(co, token), next),
    onError: (e) => {
      const list =
        e instanceof ApiError
          ? ((e.problem as { errors?: FieldError[] } | undefined)?.errors ?? [])
          : [];
      if (list.length) {
        setErrors(list);
        const first = steps.indexOf(stepOfError(list[0]?.path ?? ""));
        if (first >= 0) go(first);
      }
    },
  });

  // Autosave a second after the last edit.
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => save.mutate(answers), 1000);
    return () => clearTimeout(t);
  }, [answers, dirty, save.mutate]);

  const change = (next: Answers) => {
    setAnswers(next);
    setDirty(true);
  };
  function go(i: number) {
    if (dirty) save.mutate(answers);
    setAt(i);
    top.current?.scrollIntoView({ block: "start" });
  }
  const send = () => {
    setErrors([]);
    submit.mutate({ ...answers, tasks: namedTasks(answers).map((x) => x.t) });
  };
  const stepErrors = errors.filter((e) => stepOfError(e.path) === step);

  return (
    <div ref={top} className="flex flex-col gap-5 scroll-mt-4" data-slot="questionnaire">
      <header className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-strong text-foreground">{view.round_name}</h1>
        <p className="text-sm text-muted-foreground">
          Hi {view.respondent.name}. {view.questionnaire.label}, about {view.questionnaire.minutes}{" "}
          minutes.
          {view.expires_at ? ` The link works until ${formatDateTime(view.expires_at)}.` : ""}
        </p>
        <p
          className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground"
          data-slot="consent"
        >
          <ShieldCheckIcon className="mt-px size-3.5 shrink-0" aria-hidden={true} />
          {view.visibility}
        </p>
      </header>
      <div className="flex flex-col gap-2" data-slot="stepper">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="font-medium text-foreground">
            Step {at + 1} of {steps.length} · {STEP_LABEL[step]}
          </span>
          <span className="text-xs text-muted-foreground" aria-live="polite">
            {save.isPending
              ? "Saving…"
              : save.isError
                ? "Not saved"
                : save.isSuccess && !dirty
                  ? "Saved"
                  : ""}
          </span>
        </div>
        <Progress value={((at + 1) / steps.length) * 100} aria-label="Questionnaire progress" />
      </div>
      <section
        className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5 max-md:p-4"
        aria-label={STEP_LABEL[step]}
        data-step={step}
      >
        {stepErrors.length ? (
          <Notice tone="danger" title="A few answers are missing" role="alert">
            <ul className="list-disc pl-4">
              {stepErrors.map((e) => (
                <li key={e.path}>{e.message}</li>
              ))}
            </ul>
          </Notice>
        ) : null}
        {step === "role" ? (
          <RoleStep view={view} a={answers} onChange={change} />
        ) : step === "tasks" ? (
          <TasksStep a={answers} onChange={change} />
        ) : step === "ratings" ? (
          <RatingsStep view={view} full={full} a={answers} onChange={change} errors={errors} />
        ) : step === "capacity" ? (
          <CapacityStep view={view} a={answers} onChange={change} />
        ) : step === "management" ? (
          <ManagementStep view={view} a={answers} onChange={change} />
        ) : (
          <FrictionStep view={view} a={answers} onChange={change} />
        )}
      </section>
      {submit.isError && !errors.length ? (
        <Notice tone="danger" title="Your answers could not be sent" role="alert">
          {submit.error instanceof ApiError && submit.error.status === 410
            ? "The link has expired. Ask for a fresh one; your saved answers are kept."
            : "Try again in a moment."}
        </Notice>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <Button variant="secondary" disabled={at === 0} onClick={() => go(at - 1)}>
          <ArrowLeftIcon aria-hidden={true} />
          Back
        </Button>
        {last ? (
          <Button onClick={send} disabled={submit.isPending}>
            {submit.isPending ? "Sending…" : "Send answers"}
          </Button>
        ) : (
          <Button
            onClick={() => go(at + 1)}
            variant={stepReady(step, answers, full) ? "default" : "secondary"}
          >
            Next
            <ArrowRightIcon aria-hidden={true} />
          </Button>
        )}
      </div>
    </div>
  );
}

// Choice chips (one value, or none yet) -------------------------------------------------------

function Choices<T extends string | number>({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T | null;
  onPick: (v: T) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-sm text-foreground">{label}</legend>
      <div className="flex flex-wrap gap-1.5" data-slot="choices">
        {options.map((o) => {
          const on = o.value === value;
          return (
            <button
              key={String(o.value)}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(o.value)}
              className={`min-h-8 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                on
                  ? "border-primary bg-primary-soft text-primary-text"
                  : "border-border bg-background text-foreground hover:bg-muted"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

const SCALE = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: n === 1 ? "1 · Disagree" : n === 5 ? "5 · Agree" : String(n),
}));

function num(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

type StepProps = { view: QuestionnaireView; a: Answers; onChange: (a: Answers) => void };

function RoleStep({ view, a, onChange }: StepProps) {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="respond-role">Your role, in your words</Label>
        <Input
          id="respond-role"
          value={a.role_title ?? ""}
          maxLength={120}
          placeholder="e.g. Account manager"
          onChange={(e) => onChange({ ...a, role_title: e.target.value || null })}
        />
      </div>
      {view.choices.clarity.map((q) => (
        <Choices
          key={q.key}
          label={q.label}
          options={SCALE}
          value={(a.clarity as Record<string, number> | null)?.[q.key] ?? null}
          onPick={(v) => onChange({ ...a, clarity: { ...(a.clarity ?? {}), [q.key]: v } })}
        />
      ))}
    </>
  );
}

function TasksStep({ a, onChange }: Omit<StepProps, "view">) {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        List the work that fills your week — the big pieces, not every email. Five to ten is plenty.
      </p>
      <ol className="flex flex-col gap-2">
        {a.tasks.map((t, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: tasks are edited in place by position
          <li key={i} className="flex items-center gap-2">
            <span className="w-5 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
              {i + 1}
            </span>
            <Input
              aria-label={`Task ${i + 1}`}
              value={t.name}
              maxLength={200}
              placeholder="e.g. Screening candidates"
              onChange={(e) => onChange(editTask(a, i, { name: e.target.value }))}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove task ${i + 1}`}
              disabled={a.tasks.length === 1}
              onClick={() => onChange({ ...a, tasks: a.tasks.filter((_, j) => j !== i) })}
            >
              <Trash2Icon aria-hidden={true} />
            </Button>
          </li>
        ))}
      </ol>
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        disabled={a.tasks.length >= 30}
        onClick={() => onChange({ ...a, tasks: [...a.tasks, blankTask()] })}
      >
        <PlusIcon aria-hidden={true} />
        Add a task
      </Button>
    </>
  );
}

function RatingsStep({
  view,
  full,
  a,
  onChange,
  errors,
}: StepProps & { full: boolean; errors: FieldError[] }) {
  const named = namedTasks(a);
  if (named.length === 0)
    return <p className="text-sm text-muted-foreground">Go back and list a task first.</p>;
  const c = view.choices;
  return (
    <ol className="flex flex-col gap-6">
      {named.map(({ t, i }, k) => {
        const own = errors.filter((e) => e.path.startsWith(`tasks.${k}.`));
        return (
          <li
            key={i}
            className="flex flex-col gap-3 border-b border-border pb-5 last:border-0 last:pb-0"
            data-task={k}
          >
            <h3 className="font-medium text-foreground">{t.name}</h3>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`task-hours-${i}`}>Hours a week</Label>
              <Input
                id={`task-hours-${i}`}
                type="number"
                inputMode="decimal"
                min={0}
                max={80}
                step={0.5}
                className="w-28"
                value={t.hours_per_week ?? ""}
                onChange={(e) => onChange(editTask(a, i, { hours_per_week: num(e.target.value) }))}
              />
            </div>
            <Choices
              label="It leaves me"
              options={c.energy}
              value={t.energy}
              onPick={(v) => onChange(editTask(a, i, { energy: v }))}
            />
            <Choices
              label="I enjoy it"
              options={c.enjoy}
              value={t.enjoy}
              onPick={(v) => onChange(editTask(a, i, { enjoy: v as typeof t.enjoy }))}
            />
            <Choices
              label="My skill at it"
              options={c.skill}
              value={t.skill}
              onPick={(v) => onChange(editTask(a, i, { skill: v as typeof t.skill }))}
            />
            <Choices
              label="Area"
              options={c.pillar}
              value={t.pillar}
              onPick={(v) => onChange(editTask(a, i, { pillar: v }))}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-(--primary)"
                checked={t.someone_else}
                onChange={(e) => onChange(editTask(a, i, { someone_else: e.target.checked }))}
              />
              Someone else should be doing this
            </label>
            {full ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`task-why-${i}`}>Why does it take this time?</Label>
                <Textarea
                  id={`task-why-${i}`}
                  rows={2}
                  maxLength={400}
                  value={t.why ?? ""}
                  onChange={(e) => onChange(editTask(a, i, { why: e.target.value || null }))}
                />
              </div>
            ) : null}
            {own.length ? (
              <ul className="flex flex-col gap-0.5 text-xs text-danger-text" role="alert">
                {own.map((e) => (
                  <li key={e.path}>{e.message}</li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function CapacityStep({ view, a, onChange }: StepProps) {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="respond-capacity">Hours you can give in a normal week</Label>
        <Input
          id="respond-capacity"
          type="number"
          inputMode="decimal"
          min={0}
          max={120}
          className="w-28"
          value={a.capacity_hours ?? ""}
          onChange={(e) => onChange({ ...a, capacity_hours: num(e.target.value) })}
        />
      </div>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1.5 text-sm text-foreground">Right now my week feels</legend>
        {view.choices.load_band.map((b) => (
          <label
            key={b.value}
            className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2.5 text-sm has-checked:border-primary has-checked:bg-primary-soft"
          >
            <input
              type="radio"
              name="load_band"
              className="mt-0.5 accent-(--primary)"
              checked={a.load_band === b.value}
              onChange={() => onChange({ ...a, load_band: b.value as Answers["load_band"] })}
            />
            <span>
              <span className="font-medium text-foreground">{b.label}</span>
              <span className="block text-xs text-muted-foreground">{b.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </>
  );
}

const EMPTY_MGMT: NonNullable<Answers["management"]> = {
  mgmt_share: null,
  team_load_read: null,
  team_clarity_read: null,
  meeting_overhead_hours: null,
  bottleneck: null,
};

function ManagementStep({ view, a, onChange }: StepProps) {
  const m = a.management ?? EMPTY_MGMT;
  const set = (patch: Partial<typeof m>) => onChange({ ...a, management: { ...m, ...patch } });
  return (
    <>
      <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="mgmt-share">Share of your week spent managing (%)</Label>
          <Input
            id="mgmt-share"
            type="number"
            min={0}
            max={100}
            className="w-28"
            value={m.mgmt_share ?? ""}
            onChange={(e) => set({ mgmt_share: num(e.target.value) })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="mgmt-meetings">Hours a week in meetings</Label>
          <Input
            id="mgmt-meetings"
            type="number"
            min={0}
            max={80}
            className="w-28"
            value={m.meeting_overhead_hours ?? ""}
            onChange={(e) => set({ meeting_overhead_hours: num(e.target.value) })}
          />
        </div>
      </div>
      <Choices
        label="Your team's load, as you see it"
        options={view.choices.load_band.map((b) => ({ value: b.value, label: b.label }))}
        value={m.team_load_read}
        onPick={(v) => set({ team_load_read: v as typeof m.team_load_read })}
      />
      <Choices
        label="My team knows what they own"
        options={SCALE}
        value={m.team_clarity_read}
        onPick={(v) => set({ team_clarity_read: v })}
      />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mgmt-bottleneck">Where does work wait on you?</Label>
        <Textarea
          id="mgmt-bottleneck"
          rows={2}
          maxLength={1000}
          value={m.bottleneck ?? ""}
          onChange={(e) => set({ bottleneck: e.target.value || null })}
        />
      </div>
    </>
  );
}

function FrictionStep({ view, a, onChange }: StepProps) {
  const f = a.friction ?? { cracks: "", invisible: "", drop: "" };
  return (
    <>
      {view.choices.friction.map((q) => (
        <div key={q.key} className="flex flex-col gap-1.5">
          <Label htmlFor={`friction-${q.key}`}>{q.label}</Label>
          <Textarea
            id={`friction-${q.key}`}
            rows={3}
            value={(f as Record<string, string>)[q.key] ?? ""}
            onChange={(e) => onChange({ ...a, friction: { ...f, [q.key]: e.target.value } })}
          />
        </div>
      ))}
    </>
  );
}
